import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Link } from "react-router-dom"
import { useState, useEffect } from "react"

const fallbackArticles = [
  {
    id: 1,
    title: "Comment créer l'album photo parfait pour vos souvenirs",
    description: "Découvrez nos conseils d'experts pour créer un album photo qui raconte votre histoire de la plus belle façon.",
    categories: ["Conseils"],
    image: "/images/occasions/famille1.png",
    excerpt: "Découvrez nos conseils d'experts pour créer un album photo qui raconte votre histoire de la plus belle façon.",
    permalink: "comment-creer-album-photo-parfait"
  },
  {
    id: 2,
    title: "Les tendances photo 2024 à connaître absolument",
    description: "Explorez les dernières tendances en photographie et mise en page pour vos albums personnalisés.",
    categories: ["Tendances"],
    image: "/images/occasions/mariage1.png",
    excerpt: "Explorez les dernières tendances en photographie et mise en page pour vos albums personnalisés.",
    permalink: "tendances-photo-2024"
  },
  {
    id: 3,
    title: "Comment organiser efficacement vos photos numériques",
    description: "Des astuces pratiques pour trier, classer et retrouver facilement vos photos avant de créer votre album.",
    categories: ["Organisation"],
    image: "/images/occasions/voyage1.png",
    excerpt: "Des astuces pratiques pour trier, classer et retrouver facilement vos photos avant de créer votre album.",
    permalink: "organiser-photos-numeriques"
  },
  {
    id: 4,
    title: "10 idées créatives d'albums photo pour vos cadeaux",
    description: "Inspirez-vous de nos idées originales pour offrir un album photo personnalisé qui marquera les esprits.",
    categories: ["Inspiration"],
    image: "/images/occasions/naissance1.png",
    excerpt: "Inspirez-vous de nos idées originales pour offrir un album photo personnalisé qui marquera les esprits.",
    permalink: "idees-cadeaux-albums-photo"
  },
  {
    id: 5,
    title: "Comprendre la qualité d'impression pour vos photos",
    description: "Tout ce que vous devez savoir sur la résolution, le papier et les finitions pour des albums de qualité professionnelle.",
    categories: ["Technique"],
    image: "/images/products/portrait.png",
    excerpt: "Tout ce que vous devez savoir sur la résolution, le papier et les finitions pour des albums de qualité professionnelle.",
    permalink: "qualite-impression-photos"
  }
]

export default function Blog() {
  const [articles, setArticles] = useState(fallbackArticles)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch('/load_blog')
        if (response.ok) {
          const data = await response.json()
          setArticles(data.articles || fallbackArticles)
        } else {
          setArticles(fallbackArticles)
        }
      } catch (error) {
        console.error('Error fetching articles:', error)
        setArticles(fallbackArticles)
      } finally {
        setLoading(false)
      }
    }

    fetchArticles()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Chargement...
            </h1>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-display font-bold mt-4 mb-6 pb-2 text-gradient leading-relaxed">
            Blog
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Conseils, inspirations et astuces pour créer vos plus beaux albums photo. 
          </p>
        </div>

        {/* Featured Article */}
        {articles.length > 0 && (
          <div className="mb-16">
            <Card className="overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-background to-muted/30">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-full">
                  <img 
                    src={articles[0].image} 
                    alt={articles[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {articles[0].categories.map((category, index) => (
                      <Badge key={index} variant="secondary" className="bg-[#f9bb72] text-white hover:bg-[#f9bb72]/90">
                        {category}
                      </Badge>
                    ))}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
                    {articles[0].title}
                  </h2>
                  <p className="text-muted-foreground mb-6 text-lg">
                    {articles[0].excerpt}
                  </p>
                  <Link to={`/blog/${articles[0].permalink}`}>
                    <Button size="lg" className="w-full md:w-auto" style={{ background: 'linear-gradient(90deg, #f7bbac 0%, #f9bb72 100%)' }}>
                      Lire l'article
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        )}

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.slice(1).map((article) => (
            <Card key={article.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer border-0 bg-card/50 backdrop-blur-sm">
              <Link to={`/blog/${article.permalink}`}>
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {article.categories.map((category, index) => (
                      <Badge key={index} variant="secondary" className="bg-[#f9bb72] text-white hover:bg-[#f9bb72]/90">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-display font-semibold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm line-clamp-3">
                    {article.excerpt}
                  </p>
                  <Button 
                    variant="ghost"
                    size="sm" 
                    className="w-auto mt-2 text-[#f9bb72] hover:text-[#f7bbac] hover:bg-[#f9bb72]/10 transition-colors"
                  >
                    Lire l'article →
                  </Button>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  )
}